import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { seoArticles } from "@/mocks/seo-articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://vigorbuyspread.us";
const pagePath = "/shopping-guide";

export default function ShoppingGuidePage() {
  const guideSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Vigorbuy Spreadsheet Shopping Guide 2026 - Complete Product Discovery Tutorial",
    "description": "Your complete shopping guide for Vigorbuy Spreadsheet. Learn how to navigate all 14 product categories, use QC photos effectively, compare products, and shop smarter with organized product databases.",
    "datePublished": "2026-07-20",
    "dateModified": "2026-07-20",
    "author": { "@type": "Organization", "name": "VigorbuySpreadsheet" },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}${pagePath}` },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": "Shopping Guide", "item": `${siteUrl}${pagePath}` },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors duration-200">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700">Shopping Guide</span>
              </nav>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                Vigorbuy Spreadsheet Shopping Guide: Your Complete Product Discovery Companion
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8">
                <span>Updated July 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>15 min read</span>
              </div>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Organized%20clean%20desk%20with%20laptop%20showing%20colorful%20product%20category%20icons%20on%20screen%2C%20shopping%20bags%20and%20product%20samples%20neatly%20arranged%20around%2C%20bright%20natural%20morning%20light%2C%20modern%20retail%20planning%20workspace%2C%20warm%20neutral%20tones%2C%20lifestyle%20product%20photography%2C%20aspirational%20shopping%20setup%2C%20fresh%20and%20inviting%20atmosphere%2C%20professional%20yet%20approachable%20aesthetic&width=800&height=400&seq=shopping-guide-hero&orientation=landscape"
                  alt="Vigorbuy Spreadsheet shopping guide organized workspace"
                  className="w-full h-auto"
                />
              </div>

              <div className="prose-custom space-y-6 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p className="text-lg text-foreground-800 font-medium leading-relaxed">
                  Welcome to the ultimate Vigorbuy Spreadsheet shopping guide. Whether you are a complete beginner or an experienced spreadsheet shopper looking to refine your technique, this comprehensive guide covers every aspect of using Vigorbuy Spreadsheet to discover products efficiently, compare options intelligently, and shop with confidence.
                </p>

                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">
                  Getting Started: The Basics
                </h2>
                <p>
                  Vigorbuy Spreadsheet requires no registration, no downloads, and no technical expertise. Simply visit the main website and you will immediately see the organized category system featuring fourteen distinct product categories. The homepage provides clear navigation options — you can either browse the category grid or use the dropdown selector to jump directly to any category that interests you.
                </p>
                <p>
                  Each category functions as a standalone product catalog. When you select a category, you are taken to a dedicated page on the main website where all products within that category are displayed in an organized format. From there, clicking any product takes you directly to the shopping platform where you can complete your purchase. The entire process — from arriving at the homepage to viewing a specific product — typically takes less than thirty seconds.
                </p>

                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">
                  Category-by-Category Shopping Strategy
                </h2>

                {categories.map((cat) => (
                  <div key={cat.name}>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mt-8 mb-3">{cat.name}</h3>
                    <p>
                      The {cat.name} category is one of the most frequently visited sections on Vigorbuy Spreadsheet. When shopping this category, start by scanning the available products to get a sense of the current selection. Pay attention to product images, price ranges, and available sizes or variants. Click through to product pages for items that catch your eye, and use the browser's back button to return to the category view for comparison shopping. Regular visitors to this category often develop favorite product types and check back frequently for new additions.
                    </p>
                  </div>
                ))}

                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">
                  Smart Shopping Habits for Maximum Efficiency
                </h2>
                <div className="space-y-4">
                  {[
                    { title: "Browse with Intention", desc: "Before opening any category, clarify what you are looking for. Are you browsing for inspiration, hunting for a specific item, or comparing options? Having a clear intention reduces decision fatigue and makes your session more productive." },
                    { title: "Compare Across Categories", desc: "The real power of Vigorbuy Spreadsheet emerges when you cross-reference categories. Found a jacket you like? Check the Accessories and Bags categories for complementary pieces that complete your outfit." },
                    { title: "Check QC Photos First", desc: "When available, always review QC photos before clicking through to a product page. These real-world images help you assess actual product quality and avoid items that do not match their listing photos." },
                    { title: "Bookmark Favorites", desc: "If you find yourself returning to certain categories frequently, bookmark them in your browser for even faster access. The category URLs remain consistent, making them reliable reference points." },
                    { title: "Check Regularly for Updates", desc: "Vigorbuy Spreadsheet is refreshed on an ongoing basis. New products are added and outdated items removed regularly. Returning every few days ensures you see the latest additions." },
                  ].map((tip) => (
                    <div key={tip.title} className="flex gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-lightbulb-line text-accent-500"></i>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{tip.title}</p>
                        <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">{tip.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">
                  Safety Checklist for Spreadsheet Shopping
                </h2>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 my-6">
                  <div className="space-y-3">
                    {[
                      "Always verify product details on the shopping platform before purchasing",
                      "Check seller ratings and recent reviews for quality assurance",
                      "Use only secure, platform-provided payment methods",
                      "Review return and refund policies before completing any purchase",
                      "Never share personal or financial information outside official checkout pages",
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                          <i className="ri-shield-check-line text-primary-500 text-sm"></i>
                        </span>
                        <p className="text-sm text-foreground-700">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">
                  Explore More In-Depth Guides
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {seoArticles.slice(0, 6).map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all duration-200"
                    >
                      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-article-line text-primary-500 group-hover:text-primary-600 transition-colors"></i>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors leading-snug">
                          {article.title}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">{article.readTime} read</p>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="mt-12 p-6 md:p-8 bg-primary-50/50 rounded-2xl border border-primary-200/50 text-center">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                    Ready to Start Shopping Smarter?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                    Put this guide into practice. Explore Vigorbuy Spreadsheet now and experience the difference organized product discovery makes.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href={mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="w-full sm:w-auto px-7 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      Explore Vigorbuy Spreadsheet
                    </a>
                    <Link
                      to="/"
                      className="w-full sm:w-auto px-7 py-3 bg-background-50 border border-background-300/60 text-foreground-800 font-medium text-sm rounded-full hover:border-primary-400 hover:text-primary-700 transition-all duration-200 whitespace-nowrap"
                    >
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(guideSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
}